import React, { useEffect, useId, useRef, useState } from "react";
import type {
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  RenderTask,
} from "pdfjs-dist";
import { Banner, Button, Icon, fileUrl } from "./ui.tsx";

type LoadedDocument = { key: string; document: PDFDocumentProxy };

/** Renders an authorized local PDF without relying on a native browser plug-in. */
export function PDFPreview({
  id,
  name,
  sourceUrl,
}: {
  id?: string;
  name: string;
  sourceUrl?: string;
}) {
  const source = sourceUrl ?? (id ? fileUrl(id) : "");
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState<LoadedDocument | null>(null);
  const [loadError, setLoadError] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [width, setWidth] = useState(640);
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState("");
  const [text, setText] = useState("");
  const [textAvailable, setTextAvailable] = useState(true);
  const [showText, setShowText] = useState(false);
  const stage = useRef<HTMLDivElement>(null);
  const canvasHost = useRef<HTMLDivElement>(null);
  const textId = useId();
  const key = `${source}:${attempt}`;
  const current = loaded?.key === key ? loaded.document : null;

  useEffect(() => {
    let active = true;
    let loadingTask: PDFDocumentLoadingTask | undefined;
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
      void loadingTask?.destroy().catch(() => {});
    }, 45_000);
    setLoaded(null);
    setLoadError("");
    setRenderError("");
    setPageNumber(1);
    setZoom(1);
    setText("");
    canvasHost.current?.replaceChildren();

    async function load() {
      try {
        const pdfjs = await import("pdfjs-dist");
        if (!active) return;
        pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.mjs";
        if (!source) throw new Error("Nenhum PDF foi selecionado.");
        const response = await fetch(source, {
          credentials: "same-origin",
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(
            typeof data.error === "string"
              ? data.error
              : "Não foi possível consultar este arquivo. Confira seu acesso e tente novamente.",
          );
        }
        if (
          !response.headers.get("Content-Type")?.includes("application/pdf")
        ) {
          throw new Error(
            "O servidor não disponibilizou um arquivo PDF válido.",
          );
        }
        const bytes = await response.arrayBuffer();
        if (!active) return;
        const task = pdfjs.getDocument({
          data: new Uint8Array(bytes),
          standardFontDataUrl: "/pdf-assets/standard_fonts/",
          wasmUrl: "/pdf-assets/wasm/",
          cMapUrl: "/pdf-assets/cmaps/",
          cMapPacked: true,
          useSystemFonts: true,
          stopAtErrors: true,
          verbosity: 0,
        });
        loadingTask = task;
        const document = await task.promise;
        if (active) setLoaded({ key, document });
      } catch (error) {
        if (!active) return;
        setLoadError(
          controller.signal.aborted
            ? "A consulta demorou mais que o esperado. Confira a conexão e tente novamente."
            : error instanceof Error && error.message
              ? error.name === "PasswordException"
                ? "Este PDF exige uma senha. Baixe o original para abri-lo em um leitor compatível."
                : error.message === "Failed to fetch"
                  ? "Não foi possível conectar. Confira a internet e tente novamente."
                  : "Não foi possível abrir o PDF: " + error.message
              : "Não foi possível abrir o PDF. Você pode tentar novamente ou baixar o original.",
        );
      } finally {
        clearTimeout(timeout);
      }
    }
    void load();
    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
      void loadingTask?.destroy().catch(() => {});
    };
  }, [source, key]);

  useEffect(() => {
    const element = stage.current;
    if (!element) return;
    const measure = () => setWidth(Math.max(200, element.clientWidth));
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    if (!current) return;
    let active = true;
    let renderTask: RenderTask | undefined;
    setRendering(true);
    setRenderError("");
    setText("");
    setTextAvailable(true);
    canvasHost.current?.replaceChildren();

    async function render() {
      const page = await current!.getPage(pageNumber);
      try {
        if (!active) return;
        const base = page.getViewport({ scale: 1 });
        if (
          !Number.isFinite(base.width) ||
          !Number.isFinite(base.height) ||
          base.width <= 0 ||
          base.height <= 0
        ) {
          throw new Error("Dimensões inválidas da página.");
        }
        const fittedScale = Math.min(1.5, (width - 32) / base.width);
        const viewport = page.getViewport({ scale: fittedScale * zoom });
        if (viewport.height > 30_000)
          throw new Error("Página acima do limite de visualização.");
        // Bound raster memory while retaining crisp text on high-density displays.
        const pixelRatio = Math.min(
          window.devicePixelRatio || 1,
          2,
          Math.sqrt(16_000_000 / (viewport.width * viewport.height)),
          16_384 / viewport.width,
          16_384 / viewport.height,
        );
        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(viewport.width * pixelRatio);
        canvas.height = Math.ceil(viewport.height * pixelRatio);
        canvas.style.width = `${Math.ceil(viewport.width)}px`;
        canvas.style.height = `${Math.ceil(viewport.height)}px`;
        canvas.setAttribute("aria-hidden", "true");
        renderTask = page.render({
          canvas,
          viewport,
          transform:
            pixelRatio === 1 ? undefined : [pixelRatio, 0, 0, pixelRatio, 0, 0],
          background: "rgb(255,255,255)",
        });
        const [, content] = await Promise.all([
          renderTask.promise,
          page.getTextContent().catch(() => null),
        ]);
        if (!active) return;
        canvasHost.current?.replaceChildren(canvas);
        const extracted = content?.items
          .map((item) =>
            "str" in item ? item.str + (item.hasEOL ? "\n" : " ") : "",
          )
          .join("")
          .trim();
        setText(extracted || "");
        setTextAvailable(!!extracted);
        setRendering(false);
      } finally {
        page.cleanup();
      }
    }
    void render().catch((error) => {
      if (!active || error?.name === "RenderingCancelledException") return;
      setRendering(false);
      setRenderError(
        "Não foi possível exibir esta página. Tente novamente ou baixe o arquivo original para consultar o documento.",
      );
    });
    return () => {
      active = false;
      renderTask?.cancel();
    };
  }, [current, pageNumber, zoom, width]);

  const retry = () => setAttempt((value) => value + 1);
  const loading = !loadError && !current;
  const hasError = !!(loadError || renderError);

  return (
    <section className="pdf-preview" aria-label={`Visualização de ${name}`}>
      <div
        className="pdf-controls"
        role="group"
        aria-label="Controles do documento"
      >
        <div className="pdf-pagination">
          <Button
            type="button"
            onClick={() => setPageNumber((value) => Math.max(1, value - 1))}
            disabled={!current || pageNumber <= 1}
            aria-label="Página anterior"
          >
            Anterior
          </Button>
          <span aria-live="polite" aria-atomic="true">
            {current ? `Página ${pageNumber} de ${current.numPages}` : "PDF"}
          </span>
          <Button
            type="button"
            onClick={() =>
              setPageNumber((value) =>
                Math.min(current?.numPages || 1, value + 1),
              )
            }
            disabled={!current || pageNumber >= current.numPages}
            aria-label="Próxima página"
          >
            Próxima
          </Button>
        </div>
        <label className="pdf-zoom">
          <span>Ampliação</span>
          <select
            aria-label="Ampliação da página em relação ao ajuste à largura"
            value={zoom}
            disabled={!current}
            onChange={(event) => setZoom(Number(event.target.value))}
          >
            {[0.75, 1, 1.25, 1.5, 2].map((value) => (
              <option key={value} value={value}>
                {Math.round(value * 100)}%{value === 1 ? " · Ajustar" : ""}
              </option>
            ))}
          </select>
        </label>
        <Button
          type="button"
          onClick={() => setShowText((value) => !value)}
          aria-expanded={showText}
          aria-controls={textId}
          disabled={!current}
        >
          {showText ? "Ocultar texto" : "Consultar texto"}
        </Button>
      </div>

      {hasError && (
        <div className="pdf-error">
          <Banner kind="danger">{loadError || renderError}</Banner>
          <div className="pdf-recovery">
            <Button type="button" onClick={retry} icon="refresh">
              Tentar novamente
            </Button>
            <a className="button secondary" href={source} download>
              <Icon name="download" size={17} />
              Baixar original
            </a>
          </div>
        </div>
      )}

      <div className="pdf-stage" ref={stage} aria-busy={loading || rendering}>
        {(loading || rendering) && !hasError && (
          <div className="loading pdf-loading" role="status">
            <span className="spinner" aria-hidden="true" />
            {loading ? "Abrindo PDF…" : `Preparando página ${pageNumber}…`}
          </div>
        )}
        <div className="pdf-canvas-host" ref={canvasHost} />
      </div>

      {current && !hasError && (
        <div
          id={textId}
          className={showText ? "pdf-text" : "sr-only"}
          role="region"
          aria-label={`Texto da página ${pageNumber}`}
          aria-busy={rendering}
        >
          <h4>Texto da página {pageNumber}</h4>
          {rendering ? (
            <p>Consultando texto…</p>
          ) : textAvailable ? (
            <pre>{text}</pre>
          ) : (
            <p>
              Esta página não possui texto selecionável disponível. O original
              pode conter uma imagem digitalizada; a leitura por OCR precisa ser
              conferida.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
